import { Images, attributeTypes } from 'utils';
import { v4 as uuidv4 } from 'uuid';
import { buildSessionDataColumns, buildSessionDataTrialColumns } from '../sessionDataTable.constants';
import { formModelEnums } from '../../constants';
import {noteEnums} from "../../../../fragments/schedule/core/modals/apptDetail/constants";

export const inputTypeEnums = {
    NUMBER: 'number',
    PHONE: 'phone',
    EMAIL: 'email',
    TEXT: 'text',
    DATE: 'date',
    TIME: 'time',
};

export const selectTypeEnums = {
    MULTIPLE: 'MULTIPLE',
    SINGLE: 'SINGLE',
};
export const graphTypeEnums = {
    LINE: 'LINE',
    BAR: 'BAR',
    PIE: 'PIE',
};
export const goalGraphTypeEnums = {
    CUMULATIVE: 'CUMULATIVE',
    TARGET_OVERVIEW: 'TARGET_OVERVIEW',
};
// How a CUMULATIVE goal graph plots mastered targets — `properties.valueMode`.
export const goalGraphValueModeEnums = {
    PERCENT: 'PERCENT',
    COUNT: 'COUNT',
};

/** How a graph groups session data (`properties.sessionGroupBy`). AUTO (or absent) = by the report period length. */
export const sessionGroupByEnums = {
    AUTO: 'AUTO',
    DAY: 'DAY',
    WEEK: 'WEEK',
    MONTH: 'MONTH',
};

export const inputDateTypeList = [
    { name: 'Number', type: inputTypeEnums.NUMBER, id: inputTypeEnums.NUMBER },
    { name: 'Phone', type: inputTypeEnums.PHONE, id: inputTypeEnums.PHONE },
    { name: 'Email', type: inputTypeEnums.EMAIL, id: inputTypeEnums.EMAIL },
    { name: 'Text', type: inputTypeEnums.TEXT, id: inputTypeEnums.TEXT },
    { name: 'Date', type: inputTypeEnums.DATE, id: inputTypeEnums.DATE },
    { name: 'Time', type: inputTypeEnums.TIME, id: inputTypeEnums.TIME },
];

export const selectDateTypeList = [
    { name: 'Single Select', type: selectTypeEnums.SINGLE, id: selectTypeEnums.SINGLE },
    { name: 'Multiple Select', type: selectTypeEnums.MULTIPLE, id: selectTypeEnums.MULTIPLE },
];

/**
 * Catalog of addable blocks, built per call.
 *
 * A factory rather than a module-level array for two reasons:
 *
 * 1. Every `uuidv4()` below (option ids, session-data column ids) has to be
 *    fresh for each block the author adds. A shared array hands every Select,
 *    Checkbox and Radio in every form the exact same three option ids, and every
 *    Session Data Table the same column ids.
 * 2. The catalog itself depends on the template's model — blocks the api
 *    resolves from an appointment are only offered on models that have one.
 *
 * @param {string} [formType] the template's `model` (`formModelEnums`).
 */
export const attributesList = (formType) => {
    const isClinicalNote = formType === noteEnums?.CLINICAL_NOTES

    return [
        {
            label: 'Rich Text',
            name: 'richText',
            type: attributeTypes.RICH_TEXT,
            id: attributeTypes.RICH_TEXT,
            img: Images.attributeTextEditor,
            properties: {
                required: false,
                readOnly: false,
                width: 'Full',
            },
        },
        {
            label: 'Input',
            name: 'input',
            type: attributeTypes.INPUT,
            id: attributeTypes.INPUT,
            img: Images.attributeText,
            defaultDateType: inputTypeEnums.TEXT,
            dateTypesList: inputDateTypeList,
            // dateTypesList: [
            //     { name: 'Number', type: inputTypeEnums.NUMBER, id: inputTypeEnums.NUMBER },
            //     { name: 'Phone', type: inputTypeEnums.PHONE, id: inputTypeEnums.PHONE },
            //     { name: 'Email', type: inputTypeEnums.EMAIL, id: inputTypeEnums.EMAIL },
            //     { name: 'Text', type: inputTypeEnums.TEXT, id: inputTypeEnums.TEXT },
            //     { name: 'Date', type: inputTypeEnums.DATE, id: inputTypeEnums.DATE },
            //     { name: 'Time', type: inputTypeEnums.TIME, id: inputTypeEnums.TIME },
            // ],
            properties: {
                required: false,
                readOnly: false,
                width: 'Full',
            },
        },
        {
            label: 'Select',
            name: 'select',
            type: attributeTypes.SELECT,
            id: attributeTypes.SELECT,
            img: Images.attributeDropDown,
            border: true,
            defaultDateType: selectTypeEnums.SINGLE,
            dateTypesList: selectDateTypeList,
            // dateTypesList: [
            //     { name: 'Single Select', type: selectTypeEnums.SINGLE, id: selectTypeEnums.SINGLE },
            //     { name: 'Multiple Select', type: selectTypeEnums.MULTIPLE, id: selectTypeEnums.MULTIPLE },
            // ],
            properties: {
                required: false,
                readOnly: false,
                width: 'Full',
                options: [
                    { id: uuidv4(), name: 'Option 1', value: 'Option 1' },
                    { id: uuidv4(), name: 'Option 2', value: 'Option 2' },
                    { id: uuidv4(), name: 'Option 3', value: 'Option 3' },
                ],
            },
        },
        {
            label: 'Checkbox',
            name: 'checkbox',
            type: attributeTypes.CHECK_BOX,
            id: attributeTypes.CHECK_BOX,
            img: Images.attributeCheckBox,
            properties: {
                required: false,
                readOnly: false,
                width: 'Full',
                options: [
                    { id: uuidv4(), name: 'Option 1', value: 'Option 1' },
                    { id: uuidv4(), name: 'Option 2', value: 'Option 2' },
                    { id: uuidv4(), name: 'Option 3', value: 'Option 3' },
                ],
            },
        },
        {
            label: 'Radio',
            name: 'radio',
            type: attributeTypes.RADIO_BUTTON,
            id: attributeTypes.RADIO_BUTTON,
            img: Images.attributeRadio,
            properties: {
                required: false,
                readOnly: false,
                width: 'Full',
                options: [
                    { id: uuidv4(), name: 'Option 1', value: 'Option 1' },
                    { id: uuidv4(), name: 'Option 2', value: 'Option 2' },
                    { id: uuidv4(), name: 'Option 3', value: 'Option 3' },
                ],
            },
        },
        {
            label: 'Table',
            name: 'table',
            type: attributeTypes.TABLE,
            id: attributeTypes.TABLE,
            img: Images.attributeTable,
            border: true,
            properties: {
                required: false,
                readOnly: false,
                width: 'Full',
                table: {
                    tableHead: [
                        { id: uuidv4(), name: 'Column 1' },
                        { id: uuidv4(), name: 'Column 2' },
                        { id: uuidv4(), name: 'Column 3' },
                    ],
                    tableBody: [],
                },
            },
        },
        // Session Data Table reads the appointment's collected session data, which
        // only exists for session notes — offering it elsewhere would produce a
        // block that can never populate.
        ...(formType === formModelEnums.CLINICALNOTES
            ? [
                {
                    displayLabel: 'Data Table',
                    label: 'Session Collected Data',
                    name: 'sessionDataTable',
                    type: attributeTypes.SESSION_DATA_TABLE,
                    id: attributeTypes.SESSION_DATA_TABLE,
                    img: Images.attributeSessionDataTable,
                    border: false,
                    properties: {
                        required: false,
                        readOnly: false,
                        width: 'Full',
                        sessionDataTable: {
                            columns: buildSessionDataColumns(),
                            includeTrialDetails: false,
                            trialColumns: buildSessionDataTrialColumns(),
                        },
                    },
                },
            ]
            : []),
        {
            label: 'Signature',
            name: 'signature',
            type: attributeTypes.SIGNATURE,
            id: attributeTypes.SIGNATURE,
            img: Images.attributeSignature,
            border: isClinicalNote,
            properties: {
                required: false,
                readOnly: false,
                width: 'Full',
            },
        },
        {
            label: 'Upload',
            name: 'upload',
            type: attributeTypes.UPLOAD,
            id: attributeTypes.UPLOAD,
            img: Images.attributeUpload,
            properties: {
                required: false,
                readOnly: false,
                width: 'Full',
            },
        },

        {
            label: 'Graph',
            name: 'graph',
            type: attributeTypes.GRAPH,
            id: attributeTypes.GRAPH,
            img: Images.graphAttribute,
            border: true,
            defaultDateType: goalGraphTypeEnums.CUMULATIVE,
            properties: {
                required: false,
                readOnly: false,
                width: 'Full',
                graphType: graphTypeEnums.LINE,
                showMastery: false,
                showGoalEvent: false,
                showClientEvent: false,
                graphTypeEnums: [
                    { name: 'Line Chart', type: graphTypeEnums.LINE, id: graphTypeEnums.LINE },
                    { name: 'Bar Chart', type: graphTypeEnums.BAR, id: graphTypeEnums.BAR },
                    // { name: 'Pie Chart', type: graphTypeEnums.PIE, id: graphTypeEnums.PIE },
                ],
            },
        },
        {
            label: 'Target',
            name: 'target',
            type: attributeTypes.TARGET,
            id: attributeTypes.TARGET,
            img: Images.attributeGoal,
            properties: {
                required: false,
                readOnly: false,
                width: 'Full',
                showGraph: false,
                showBaseline: false,
                graphType: graphTypeEnums.LINE,
                showMastery: false,
                showGoalEvent: false,
                showClientEvent: false,
                graphTypeEnums: [
                    { name: 'Line Chart', type: graphTypeEnums.LINE, id: graphTypeEnums.LINE },
                    { name: 'Bar Chart', type: graphTypeEnums.BAR, id: graphTypeEnums.BAR },
                ],
            },
        },
    ];
}
